import { GoogleGenAI, Type } from "@google/genai";
import { Lead, VerifiedSource, Industry, Province } from "../types";

const getApiKey = () => process.env.API_KEY || "";

/**
 * GENERATES DATA LOCALLY - USES ZERO TOKENS
 * Use this to test the app when API limits are hit.
 */
export const generateMockLeads = (industry: string, province: string, count: number): Lead[] => {
  const mockNames = ["Standard", "Elite", "Apex", "Global", "Direct", "Impact", "Core", "Prime"];
  const mockSectors = ["Group", "Solutions", "Services", "Partners", "Holdings"];
  
  return Array.from({ length: count }).map(() => {
    const name = mockNames[Math.floor(Math.random() * mockNames.length)];
    const sector = mockSectors[Math.floor(Math.random() * mockSectors.length)];
    const businessName = `${name} ${industry} ${sector}`;
    const isCatch = Math.random() > 0.6;
    const slug = businessName.toLowerCase().replace(/\s/g, '');
    
    return {
      id: Math.random().toString(36).substr(2, 9),
      dateScraped: new Date().toISOString().split('T')[0],
      businessName,
      industry: industry as Industry,
      province: province as Province,
      companySize: "Small (11-50 employees)",
      website: isCatch ? "NONE" : `https://www.${slug}.co.za`,
      contactNumber: "011 " + Math.floor(1000000 + Math.random() * 9000000),
      email: `contact@${slug}.co.za`,
      vettingScore: Math.floor(Math.random() * 4) + 6,
      vettingBreakdown: [
        { category: "Social Presence", score: 8, reason: "Active Facebook but no conversion path." },
        { category: "Web Authority", score: 3, reason: "Site is non-existent or 5+ years old." }
      ],
      status: 'Vetted',
      notes: "Generated in Cold Forge mode. No AI tokens used. High probability for digital intervention.",
      isCatchOfTheDay: isCatch,
      isArchived: false,
      activityLog: [],
      validationStatus: 'Unverified',
      verifiedSources: [{ title: "Local Registry Search", uri: "#" }],
      facebookUrl: isCatch ? undefined : `https://facebook.com/${slug}`,
      instagramUrl: isCatch ? undefined : `https://instagram.com/${slug}`,
      linkedInUrl: isCatch ? undefined : `https://linkedin.com/company/${slug}`
    };
  });
};

/**
 * Scout leads using Google Search grounding.
 * UPDATED: Added WhatsApp requirement logic and explicit social link harvesting.
 */
export const scoutLeads = async (
  industry: string, 
  province: string, 
  companySize: string,
  targetCount: number = 6,
  scoutingMode: string = "Standard Transformation",
  useDeepThinking: boolean = false,
  feedbackContext?: string,
  mustHaveWhatsApp: boolean = false
): Promise<Lead[]> => {
  const apiKey = getApiKey();
  if (!apiKey) throw new Error("API Key Missing.");

  try {
    const ai = new GoogleGenAI({ apiKey });
    const prompt = `Find ${targetCount} real and active businesses in the ${industry} industry located in ${province}, South Africa.
    Focus specifically on businesses of size: ${companySize}.
    
    CRITICAL REQUIREMENTS:
    - ${mustHaveWhatsApp ? "The business MUST have a valid WhatsApp-enabled contact number (mobile format starting with 0 or +27)." : ""}
    - Exhaustively harvest the following digital touchpoints: 
      1. Official website URL (return "NONE" if missing or broken)
      2. Direct business email address
      3. Facebook Page URL
      4. Instagram Profile URL
      5. LinkedIn Company Page URL
    
    Identify businesses with clear 'digital gaps' (e.g., poor web presence, inactive social media, or no online booking/sales).
    Evaluate them with a vettingScore (0-10) based on their potential for a digital transformation service sell.
    Return a structured JSON array matching the schema.`;
    
    const modelName = useDeepThinking ? 'gemini-3-pro-preview' : 'gemini-3-flash-preview';

    const response = await ai.models.generateContent({
      model: modelName,
      contents: prompt,
      config: {
        responseMimeType: "application/json",
        tools: [{ googleSearch: {} }],
        responseSchema: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              businessName: { type: Type.STRING },
              website: { type: Type.STRING },
              contactNumber: { type: Type.STRING },
              email: { type: Type.STRING },
              facebookUrl: { type: Type.STRING },
              instagramUrl: { type: Type.STRING },
              linkedInUrl: { type: Type.STRING },
              vettingScore: { type: Type.NUMBER },
              notes: { type: Type.STRING },
              isCatchOfTheDay: { type: Type.BOOLEAN },
              vettingBreakdown: {
                type: Type.ARRAY,
                items: {
                  type: Type.OBJECT,
                  properties: {
                    category: { type: Type.STRING },
                    score: { type: Type.NUMBER },
                    reason: { type: Type.STRING }
                  },
                  required: ["category", "score", "reason"]
                }
              }
            },
            required: ["businessName", "website", "contactNumber", "email", "vettingScore", "notes", "isCatchOfTheDay"]
          }
        }
      },
    });

    const text = response.text;
    if (!text) throw new Error("Empty response from Scouting Agent.");
    
    const data = JSON.parse(text);
    
    const groundingChunks = response.candidates?.[0]?.groundingMetadata?.groundingChunks || [];
    const verifiedSources: VerifiedSource[] = groundingChunks
      .filter((chunk: any) => chunk.web)
      .map((chunk: any) => ({
        title: chunk.web.title || "Search Result",
        uri: chunk.web.uri
      }));

    return data.map((item: any) => ({
      ...item,
      id: Math.random().toString(36).substr(2, 9),
      dateScraped: new Date().toISOString().split('T')[0],
      industry: industry as Industry,
      province: province as Province,
      companySize: companySize,
      status: 'Vetted',
      isArchived: false,
      activityLog: [],
      validationStatus: 'Unverified',
      verifiedSources: verifiedSources
    }));
  } catch (error: any) {
    if (error.message?.includes("429")) {
      throw new Error("QUOTA EXHAUSTED: Switch to 'Cold Forge' in settings to continue without tokens.");
    }
    throw error;
  }
};

export const generateOutreach = async (
  lead: Lead,
  vettingPoints: string[]
): Promise<{ whatsapp: string, emailSubject: string, emailBody: string, socialDm: string }> => {
  const apiKey = getApiKey();
  if (!apiKey) throw new Error("API Key Missing.");

  const ai = new GoogleGenAI({ apiKey });
  const prompt = `Create highly personalized outreach messages for the following South African business:
  Business Name: ${lead.businessName}
  Industry: ${lead.industry}
  Province: ${lead.province}
  Website: ${lead.website}
  Pain Points: ${vettingPoints.join(", ")}
  Tone: Professional, expert, and uniquely South African. Focus on solving the specific digital gaps found during scouting.`;

  const response = await ai.models.generateContent({
    model: 'gemini-3-pro-preview',
    contents: prompt,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          whatsapp: { type: Type.STRING },
          emailSubject: { type: Type.STRING },
          emailBody: { type: Type.STRING },
          socialDm: { type: Type.STRING },
        },
        required: ["whatsapp", "emailSubject", "emailBody", "socialDm"],
      },
    },
  });

  const text = response.text;
  if (!text) throw new Error("Outreach generation failed.");
  return JSON.parse(text);
};